import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Trophy, Shield, Sparkles, Mic, Crown, Camera, FileText, UserCog, GraduationCap } from "lucide-react";
import { MbtiBadge, clubInfo, MBTI_GROUPS } from "@/lib/mbti";
import { Avatar } from "@/components/PhotoUpload";
import ConversionSummary from "@/components/ConversionSummary";
import { useAuth } from "@/context/AuthContext";

export default function Rankings() {
  const [tab, setTab] = useState("players");
  const [players, setPlayers] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [teams, setTeams] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [social, setSocial] = useState(null);
  const [coaches, setCoaches] = useState(null);
  const [coachesScope, setCoachesScope] = useState("week");
  const { user } = useAuth();

  // Consultor e Coach veem o próprio clube por defeito; podem expandir para "Todos".
  // Admin e Staff veem sempre todos os clubes (sem toggle).
  const canFilterByClub = (user?.role === "consultant" || user?.role === "coach") && Boolean(user?.club);
  const [playersScope, setPlayersScope] = useState(canFilterByClub ? "myClub" : "all");
  const [timeScope, setTimeScope] = useState("edition"); // "edition" | "all-time"

  useEffect(() => {
    const clubParam = canFilterByClub
      ? `scope=${playersScope === "myClub" ? "my_club" : "all"}`
      : "";
    const timeParam = `time_scope=${timeScope}`;
    const qs = [clubParam, timeParam].filter(Boolean).join("&");
    api.get(`/rankings/players?${qs}`).then((r) => setPlayers(r.data)).catch(() => {});
    api.get(`/rankings/assistants`).then((r) => setAssistants(r.data)).catch(() => {});
    api.get("/rankings/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/rankings/clubs").then((r) => setClubs(r.data)).catch(() => {});
    api.get("/rankings/social").then((r) => setSocial(r.data)).catch(() => {});
  }, [playersScope, canFilterByClub, timeScope]);

  useEffect(() => {
    if (tab === "coaches") {
      api.get(`/rankings/coaches?scope=${coachesScope}`).then((r) => setCoaches(r.data)).catch(() => {});
    }
  }, [tab, coachesScope]);

  // Server already applies the club filter; render rows as-is.
  const visiblePlayers = players;

  return (
    <div className="space-y-8 kw-fade-up" data-testid="rankings-page">
      <div>
        <div className="kw-overline mb-2">Classificações</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
          Rankings <span className="text-[#E50000]">Oficiais</span>
        </h1>
      </div>

      {/* Sticky filter zone — period + tabs stay visible on scroll */}
      <div className="sticky top-14 md:top-12 z-10 -mx-4 md:-mx-10 px-4 md:px-10 py-3 space-y-3 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#1F1F1F]" data-testid="rankings-sticky-filters">
        {/* Time-scope toggle: Edição actual vs All-time */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="time-scope-toggle">
          <span className="kw-overline text-[10px]">Período:</span>
          <button
            onClick={() => setTimeScope("edition")}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
              timeScope === "edition"
                ? "border-[#E50000] bg-[#E50000] text-white"
                : "border-[#222] text-[#A3A3A3] hover:text-white"
            }`}
            data-testid="scope-edition"
          >
            Edição actual
          </button>
          <button
            onClick={() => setTimeScope("all-time")}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
              timeScope === "all-time"
                ? "border-[#E50000] bg-[#E50000] text-white"
                : "border-[#222] text-[#A3A3A3] hover:text-white"
            }`}
            data-testid="scope-all-time"
          >
            All-Time
          </button>
        </div>

        <div className="flex gap-2 border-b border-[#222] overflow-x-auto">
          <TabBtn active={tab === "players"} onClick={() => setTab("players")} icon={Trophy} testid="tab-players">Consultores</TabBtn>
          <TabBtn active={tab === "assistants"} onClick={() => setTab("assistants")} icon={UserCog} testid="tab-assistants">Assistentes</TabBtn>
          <TabBtn active={tab === "teams"} onClick={() => setTab("teams")} icon={Shield} testid="tab-teams">Equipas KW</TabBtn>
          <TabBtn active={tab === "clubs"} onClick={() => setTab("clubs")} icon={Sparkles} testid="tab-clubs">Clubes MBTI</TabBtn>
          <TabBtn active={tab === "social"} onClick={() => setTab("social")} icon={Mic} testid="tab-social">Visibilidade Social</TabBtn>
          <TabBtn active={tab === "coaches"} onClick={() => setTab("coaches")} icon={GraduationCap} testid="tab-coaches">Secretários</TabBtn>
        </div>
      </div>

      {tab === "players" && (
        <>
          {canFilterByClub && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="players-scope-toggle">
              <span className="kw-overline text-[10px]">Vista:</span>
              <button
                onClick={() => setPlayersScope("myClub")}
                className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
                  playersScope === "myClub"
                    ? "border-[#E50000] bg-[#E50000] text-white"
                    : "border-[#222] text-[#A3A3A3] hover:text-white"
                }`}
                data-testid="scope-my-club"
              >
                Meu Clube ({user.club})
              </button>
              <button
                onClick={() => setPlayersScope("all")}
                className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
                  playersScope === "all"
                    ? "border-[#E50000] bg-[#E50000] text-white"
                    : "border-[#222] text-[#A3A3A3] hover:text-white"
                }`}
                data-testid="scope-all"
              >
                Todos
              </button>
              <span className="text-[10px] text-[#737373] ml-2">
                {visiblePlayers.length} consultor{visiblePlayers.length === 1 ? "" : "es"}
              </span>
            </div>
          )}
          <PlayersTable rows={visiblePlayers} />
        </>
      )}
      {tab === "assistants" && (
        <div className="space-y-3">
          <div className="kw-card p-4 bg-[#FFB020]/5 border-[#FFB020]/30">
            <div className="kw-overline text-[#FFB020] mb-1">Campeonato das Assistentes</div>
            <p className="text-xs text-[#A3A3A3]">
              As assistentes não coopetem com os consultores. Os pontos delas vêm de contactos, reuniões,
              posts sociais, vídeos humanizados e Growth Shares (recrutamento). Sem angariações nem vendas.
            </p>
          </div>
          <AssistantsTable rows={assistants} />
        </div>
      )}
      {tab === "teams" && <TeamsTable rows={teams} />}
      {tab === "clubs" && (
        <div className="space-y-6">
          <ConversionSummary highlightClub={user?.role === "coach" ? user?.club : null} />
          <ClubsView rows={clubs} />
        </div>
      )}
      {tab === "social" && <SocialView data={social} />}
      {tab === "coaches" && (
        <CoachesView data={coaches} scope={coachesScope} setScope={setCoachesScope} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-6 py-3 font-display uppercase tracking-wider font-bold text-sm border-b-2 -mb-px transition-colors ${
        active ? "border-[#E50000] text-white" : "border-transparent text-[#737373] hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4 inline mr-2" /> {children}
    </button>
  );
}

const POINT_PARTS = [
  { key: "base_points", label: "Reportes semanais" },
  { key: "presence_balance", label: "Saldo de presença" },
  { key: "consistency_bonus", label: "Bónus de consistência" },
  { key: "identity_bonus_pts", label: "Perfil de identidade (+3 por campo)" },
  { key: "reflection_pts", label: "Reflexões de formação (+3 cada)" },
  { key: "card_points", label: "Cartões brancos" },
  { key: "golden_bonus_pts", label: "Padrinho Dourado" },
  { key: "team_presence_bonus", label: "Bónus de presença da equipa" },
];

function PointsBreakdown({ p, onClose }) {
  const parts = POINT_PARTS.map((x) => ({ ...x, value: Math.round(p[x.key] || 0) })).filter((x) => x.value);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="kw-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="points-breakdown">
        <div className="kw-overline text-[#E50000]">De onde vêm os pontos</div>
        <h3 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">{p.name}</h3>
        <div className="mt-4 divide-y divide-[#1A1A1A]">
          {parts.length === 0 && <div className="text-sm text-[#737373] py-2">Ainda sem pontos.</div>}
          {parts.map((x) => (
            <div key={x.key} className="flex items-baseline justify-between gap-4 py-2">
              <span className="text-sm text-[#A3A3A3]">{x.label}</span>
              <span className="font-display text-lg font-black">{x.value}</span>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-4 pt-3">
            <span className="text-xs uppercase tracking-widest font-bold">Total</span>
            <span className="font-display text-2xl font-black text-[#E50000]">{Math.round(p.total_points)}</span>
          </div>
        </div>
        <button onClick={onClose} className="kw-btn-secondary w-full mt-5" data-testid="points-breakdown-close">Fechar</button>
      </div>
    </div>
  );
}

function PlayersTable({ rows }) {
  const [detail, setDetail] = useState(null);
  return (
    <>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2" data-testid="players-legend">
        V vendas · A angariações · R reuniões · C contactos · P posts sociais · VH vídeos humanizados
        {" · "}clica nos pontos para ver de onde vêm
      </div>
      {/* Desktop / Tablet table */}
      <div className="kw-card overflow-x-auto hidden md:block" data-testid="players-ranking-table">
        <table className="w-full text-left text-sm">
          <thead className="kw-thead-sticky border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-4 w-16 kw-th-sticky-col">#</th>
              <th className="p-4 w-12"></th>
              <th className="p-4">Consultor</th>
              <th className="p-4">Perfil</th>
              <th className="p-4">Clube</th>
              <th className="p-4 text-right">Pontos</th>
              <th className="p-4 text-right" title="Vendas">V</th>
              <th className="p-4 text-right" title="Angariações">A</th>
              <th className="p-4 text-right" title="Reuniões">R</th>
              <th className="p-4 text-right" title="Contactos">C</th>
              <th className="p-4 text-right" title="Posts sociais">P</th>
              <th className="p-4 text-right" title="Vídeos humanizados">VH</th>
              <th className="p-4 text-right" title="Taxa ponderada: 20% R/C + 60% A/R + 20% V/A">Conv. Pond.</th>
              <th className="p-4 text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="14" className="p-8 text-center text-[#737373]">Sem dados ainda.</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.player_id} className="border-b border-[#1A1A1A] hover:bg-[#141414] bg-[#1A1A1A]">
                <td className="p-4 font-display text-2xl font-black kw-col-sticky">
                  <span className={p.rank === 1 ? "text-[#E50000]" : ""}>{String(p.rank).padStart(2, "0")}</span>
                </td>
                <td className="p-4"><Avatar photoUrl={p.photo_url} name={p.name} size={36} /></td>
                <td className="p-4 font-semibold">{p.name}</td>
                <td className="p-4"><MbtiBadge mbti={p.mbti} label={p.personality_label} compact /></td>
                <td className="p-4">{p.club ? <MbtiBadge club={p.club} /> : <span className="text-[#737373] text-xs">—</span>}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setDetail(p)}
                    className="font-display text-xl font-black hover:text-[#E50000] transition-colors underline decoration-dotted decoration-[#444] underline-offset-4"
                    title="Ver de onde vêm os pontos"
                    data-testid={`points-detail-${p.player_id}`}>
                    {Math.round(p.total_points)}
                  </button>
                </td>
                <td className="p-4 text-right">{p.totals.vendas}</td>
                <td className="p-4 text-right">{p.totals.angariacoes}</td>
                <td className="p-4 text-right">{p.totals.reunioes}</td>
                <td className="p-4 text-right">{p.totals.contactos}</td>
                <td className="p-4 text-right">{p.totals.social_posts || 0}</td>
                <td className="p-4 text-right">{p.totals.humanized_videos || 0}</td>
                <td className="p-4 text-right font-mono text-xs text-[#E50000]">{((p.weighted_conversion ?? 0) * 100).toFixed(1)}%</td>
                <td className="p-4 text-center">
                  {p.expelled ? <Pill color="red" title={p.expelled_reason || "Excluído da edição"}>Excluído</Pill>
                    : p.mvp_eligible ? <Pill color="green">MVP Eleg.</Pill>
                      : <Pill title={p.mvp_blocked_reason || undefined}>Ativo</Pill>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3" data-testid="players-ranking-cards">
        {rows.length === 0 && <div className="kw-card p-6 text-center text-[#737373]">Sem dados ainda.</div>}
        {rows.map((p) => (
          <div key={p.player_id} className="kw-card p-4 flex items-center gap-3">
            <div className={`font-display text-3xl font-black w-10 text-center ${p.rank === 1 ? "text-[#E50000]" : "text-white"}`}>
              {String(p.rank).padStart(2, "0")}
            </div>
            <Avatar photoUrl={p.photo_url} name={p.name} size={44} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {p.mbti && <MbtiBadge mbti={p.mbti} label={p.personality_label} compact />}
                {p.club && <MbtiBadge club={p.club} />}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                V {p.totals.vendas} · A {p.totals.angariacoes} · R {p.totals.reunioes} · C {p.totals.contactos}
                {" · "}P {p.totals.social_posts || 0} · VH {p.totals.humanized_videos || 0}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#E50000] mt-1 font-mono">
                Conv. ponderada {((p.weighted_conversion ?? 0) * 100).toFixed(1)}%
              </div>
            </div>
            <button className="text-right" onClick={() => setDetail(p)} data-testid={`points-detail-m-${p.player_id}`}>
              <div className="font-display text-2xl font-black">{Math.round(p.total_points)}</div>
              <div className="kw-overline">Pts</div>
            </button>
          </div>
        ))}
      </div>

      {detail && <PointsBreakdown p={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function AssistantsTable({ rows }) {
  return (
    <>
      <div className="kw-card overflow-x-auto hidden md:block" data-testid="assistants-ranking-table">
        <table className="w-full text-left text-sm">
          <thead className="kw-thead-sticky border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-4 w-16 kw-th-sticky-col">#</th>
              <th className="p-4 w-12"></th>
              <th className="p-4">Assistente</th>
              <th className="p-4">Perfil</th>
              <th className="p-4">Clube</th>
              <th className="p-4 text-right">Pontos</th>
              <th className="p-4 text-right" title="Reuniões">R</th>
              <th className="p-4 text-right" title="Contactos">C</th>
              <th className="p-4 text-right" title="Posts sociais">Posts</th>
              <th className="p-4 text-right" title="Vídeos humanizados">Vídeos</th>
              <th className="p-4 text-right" title="Growth Shares (recrutamentos)">GS</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="11" className="p-8 text-center text-[#737373]">Sem assistentes no ranking ainda.</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.player_id} className="border-b border-[#1A1A1A] hover:bg-[#141414] bg-[#1A1A1A]">
                <td className="p-4 font-display text-2xl font-black kw-col-sticky">
                  <span className={p.rank === 1 ? "text-[#FFB020]" : ""}>{String(p.rank).padStart(2, "0")}</span>
                </td>
                <td className="p-4"><Avatar photoUrl={p.photo_url} name={p.name} size={36} /></td>
                <td className="p-4 font-semibold">{p.name}</td>
                <td className="p-4"><MbtiBadge mbti={p.mbti} label={p.personality_label} compact /></td>
                <td className="p-4">{p.club ? <MbtiBadge club={p.club} /> : <span className="text-[#737373] text-xs">—</span>}</td>
                <td className="p-4 text-right font-display text-xl font-black">{Math.round(p.total_points)}</td>
                <td className="p-4 text-right">{p.totals.reunioes}</td>
                <td className="p-4 text-right">{p.totals.contactos}</td>
                <td className="p-4 text-right">{p.totals.social_posts || 0}</td>
                <td className="p-4 text-right">{p.totals.humanized_videos || 0}</td>
                <td className="p-4 text-right">{(p.totals.growth_shares || 0) + (p.totals.growth_shares_internal || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile cards */}
      <div className="md:hidden grid gap-3" data-testid="assistants-ranking-cards">
        {rows.length === 0 && <div className="kw-card p-6 text-center text-[#737373]">Sem assistentes no ranking ainda.</div>}
        {rows.map((p) => (
          <div key={p.player_id} className="kw-card p-4 flex items-center gap-3">
            <div className={`font-display text-3xl font-black w-10 text-center ${p.rank === 1 ? "text-[#FFB020]" : "text-white"}`}>
              {String(p.rank).padStart(2, "0")}
            </div>
            <Avatar photoUrl={p.photo_url} name={p.name} size={44} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {p.mbti && <MbtiBadge mbti={p.mbti} label={p.personality_label} compact />}
                {p.club && <MbtiBadge club={p.club} />}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                R {p.totals.reunioes} · C {p.totals.contactos} · Posts {p.totals.social_posts || 0} · Vídeos {p.totals.humanized_videos || 0}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-black">{Math.round(p.total_points)}</div>
              <div className="kw-overline">Pts</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TeamsTable({ rows }) {
  return (
    <>
      <div className="kw-card p-4 flex items-start gap-3 border-l-4 border-[#E50000]/50" data-testid="teams-ranking-legend">
        <div className="flex-1 text-xs text-[#A3A3A3]">
          <div className="kw-overline text-[#E50000] mb-1">Como se calcula o Team Growth Score?</div>
          <p className="leading-relaxed">
            KPI composto ponderado: <span className="text-white font-mono">50% Pts/Cons + 20% Conv×100 + 15% Growth Sh×10 + 15% Reports Completos/N × 5</span>.
            Rewards eficiência individual, qualidade comercial, recrutamento e disciplina de reporte. Hover nas colunas da tabela para detalhes de cada KPI.
          </p>
        </div>
      </div>
      <div className="kw-card overflow-x-auto hidden md:block" data-testid="teams-ranking-table">
        <table className="w-full text-left text-sm">
          <thead className="kw-thead-sticky border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-4 w-16">#</th>
              <th className="p-4">Equipa</th>
              <th className="p-4 text-right">Consultores</th>
              <th className="p-4 text-right">Pontos</th>
              <th className="p-4 text-right cursor-help" title="Pontos totais da equipa ÷ nº de consultores activos. Serve para comparar eficiência entre equipas de tamanhos diferentes.">Pts/Cons.</th>
              <th className="p-4 text-right">Vendas</th>
              <th className="p-4 text-right cursor-help" title="Growth Shares — recrutamento/crescimento (interno +20 pts, externo +100 pts).">Growth Sh.</th>
              <th className="p-4 text-right cursor-help" title="Taxa de conversão = Vendas ÷ Contactos.">Conv.</th>
              <th
                className="p-4 text-right cursor-help"
                title={"Team Growth Score — KPI composto ponderado:\n\n  50%  Pts/Cons        (eficiência individual)\n  20%  Conv. × 100    (qualidade comercial)\n  15%  Growth Sh × 10 (recrutamento)\n  15%  Reports Completos/N × 5 (disciplina)\n\nExemplo: Pts/Cons=271.8 · Conv=0% · GS=2 · Reports≈1\n  = 271.8×0.5 + 0×0.2 + 20×0.15 + 5×0.15 ≈ 139"}
              >
                Team Growth Score
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="9" className="p-8 text-center text-[#737373]">Sem equipas ainda.</td></tr>
            )}
            {rows.map((t) => (
              <tr key={t.team_id} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                <td className="p-4 font-display text-2xl font-black">
                  <span className={t.rank === 1 ? "text-[#E50000]" : ""}>{String(t.rank).padStart(2, "0")}</span>
                </td>
                <td className="p-4 font-semibold">{t.name}</td>
                <td className="p-4 text-right">{t.num_consultants}</td>
                <td className="p-4 text-right">{Math.round(t.total_points)}</td>
                <td className="p-4 text-right">{t.points_per_consultant.toFixed(1)}</td>
                <td className="p-4 text-right">{t.total_vendas}</td>
                <td className="p-4 text-right">{t.total_growth_shares}</td>
                <td className="p-4 text-right font-mono text-xs">{(t.conversion_rate * 100).toFixed(1)}%</td>
                <td className="p-4 text-right font-display text-xl font-black text-[#E50000]">{t.team_growth_score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3" data-testid="teams-ranking-cards">
        {rows.length === 0 && <div className="kw-card p-6 text-center text-[#737373]">Sem equipas ainda.</div>}
        {rows.map((t) => (
          <div key={t.team_id} className="kw-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className={`font-display text-2xl font-black ${t.rank === 1 ? "text-[#E50000]" : ""}`}>
                  {String(t.rank).padStart(2, "0")}
                </div>
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#737373]">{t.num_consultants} consultores</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-black text-[#E50000]">{t.team_growth_score}</div>
                <div className="kw-overline">Score</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#222] text-center">
              <div><div className="font-display text-lg font-black">{Math.round(t.total_points)}</div><div className="kw-overline">Pts</div></div>
              <div><div className="font-display text-lg font-black">{t.total_vendas}</div><div className="kw-overline">V</div></div>
              <div><div className="font-display text-lg font-black">{(t.conversion_rate * 100).toFixed(1)}%</div><div className="kw-overline">Conv</div></div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ClubsView({ rows }) {
  // Always show all 4 clubs even if no players; backend returns only those with players, so merge
  const byName = Object.fromEntries(rows.map((r) => [r.club, r]));
  const merged = MBTI_GROUPS.map((g) => {
    const data = byName[g.club] || {};
    return {
      club: g.club,
      color: g.color,
      bg: g.bg,
      border: g.border,
      description: g.description,
      num_players: data.num_players || 0,
      num_competing: data.num_competing || 0,
      num_substitutes: data.num_substitutes || 0,
      below_min: data.below_min,
      above_max: data.above_max,
      total_points: data.total_points || 0,
      avg_points: data.avg_points || 0,
      total_vendas: data.total_vendas || 0,
      total_angariacoes: data.total_angariacoes || 0,
      total_reunioes: data.total_reunioes || 0,
      total_contactos: data.total_contactos || 0,
      total_growth_shares: data.total_growth_shares || 0,
      types: g.types,
      rank: data.rank,
    };
  });

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="clubs-ranking">
      {merged.map((c, i) => (
        <div key={c.club} className="kw-card p-6 kw-card-hover" style={{ borderColor: c.border }}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="kw-overline" style={{ color: c.color }}>
                {c.rank ? `#${String(c.rank).padStart(2, "0")} Ranking` : "Sem dados"}
              </div>
              <h3 className="font-display text-3xl font-black uppercase tracking-tight" style={{ color: c.color }}>
                {c.club}
              </h3>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl font-black">{Math.round(c.total_points)}</div>
              <div className="kw-overline">Pontos</div>
            </div>
          </div>
          <p className="text-xs text-[#A3A3A3] mb-2">{c.description}</p>
          <div className="mb-4 text-[10px] uppercase tracking-widest font-bold">
            Composição: <span className={c.below_min ? "text-[#FF6B6B]" : c.above_max ? "text-[#FFB020]" : "text-[#00FF88]"}>
              {c.num_competing}/8 concorrentes
              {c.below_min ? " · plantel incompleto" : c.above_max ? " · acima da quota" : " · ok"}
            </span>
            <span className="text-[#737373] ml-2">· conta os 8 que coopetem</span>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {c.types.map((t) => (
              <div key={t.code} className="border border-[#222] p-2 text-center">
                <div className="font-mono text-xs font-bold" style={{ color: c.color }}>{t.code}</div>
                <div className="text-[10px] text-[#A3A3A3]">{t.label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-2 text-center pt-3 border-t border-[#222]">
            <Stat label="Coopet." value={c.num_competing} />
            <Stat label="Vendas" value={c.total_vendas} />
            <Stat label="Ang." value={c.total_angariacoes} />
            <Stat label="Reun." value={c.total_reunioes} />
            <Stat label="GS" value={c.total_growth_shares} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-xl font-black">{value}</div>
      <div className="kw-overline">{label}</div>
    </div>
  );
}

function Pill({ children, color, title }) {
  const cls = {
    red: "bg-[#E50000]/15 text-[#FF6B6B] border-[#E50000]/40",
    green: "bg-[#00FF00]/10 text-[#00FF88] border-[#00FF88]/30",
  }[color] || "bg-[#1A1A1A] text-[#A3A3A3] border-[#333]";
  return <span title={title} className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border ${cls} ${title ? "cursor-help" : ""}`}>{children}</span>;
}

function SocialView({ data }) {
  if (!data) {
    return <div className="kw-card p-6 text-center text-[#737373]" data-testid="social-loading">A carregar visibilidade social…</div>;
  }
  const { rows = [], voice_of_kw, month_label } = data;
  const hasAny = rows.some((r) => r.social_points > 0);

  return (
    <div className="space-y-6" data-testid="social-ranking">
      {/* Hero: Voice of KW (medal) */}
      <div
        className="kw-card overflow-hidden relative"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 0%, rgba(229,0,0,0.18) 0%, transparent 55%), radial-gradient(circle at 95% 100%, rgba(229,0,0,0.10) 0%, transparent 45%)",
        }}
        data-testid="voice-of-kw-card"
      >
        <div className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="flex-shrink-0 relative">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full border-4 border-[#E50000] flex items-center justify-center bg-[#0A0A0A] relative kw-pulse">
              {voice_of_kw ? (
                <Avatar photoUrl={voice_of_kw.photo_url} name={voice_of_kw.name} size={104} />
              ) : (
                <Mic className="w-12 h-12 text-[#E50000]" />
              )}
              <div className="absolute -top-2 -right-2 bg-[#E50000] text-white rounded-full p-2 shadow-[0_0_24px_rgba(229,0,0,0.7)]">
                <Crown className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="kw-overline text-[#E50000] mb-1">Voice of KW · {month_label}</div>
            <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
              {voice_of_kw ? voice_of_kw.name : "Sem voz ainda este mês"}
            </h2>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
              Medalha mensal atribuída ao consultor com mais pontos de <strong className="text-white">marca pessoal</strong> (posts redes sociais + vídeos humanizados). Cria conteúdo e mostra a tua cara — a KW Lead nota.
            </p>
            {voice_of_kw && (
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                {voice_of_kw.club && <MbtiBadge club={voice_of_kw.club} />}
                {voice_of_kw.mbti && <MbtiBadge mbti={voice_of_kw.mbti} label={voice_of_kw.personality_label} compact />}
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[#A3A3A3]">
                  <FileText className="w-3 h-3" /> {voice_of_kw.social_posts} posts
                </span>
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[#A3A3A3]">
                  <Camera className="w-3 h-3" /> {voice_of_kw.humanized_videos} vídeos
                </span>
              </div>
            )}
          </div>
          {voice_of_kw && (
            <div className="text-right md:border-l md:border-[#222] md:pl-6">
              <div className="font-display text-5xl md:text-6xl font-black text-[#E50000] leading-none" data-testid="voice-month-points">
                {Math.round(voice_of_kw.month_points)}
              </div>
              <div className="kw-overline mt-1">Pts este mês</div>
            </div>
          )}
        </div>
      </div>

      {/* Scoring callout */}
      <div className="kw-card p-4 flex flex-wrap items-center gap-4 text-xs text-[#A3A3A3]">
        <FileText className="w-4 h-4 text-[#E50000]" />
        <span><strong className="text-white">+5 pts</strong> por post (LinkedIn, Insta, FB, TikTok)</span>
        <span className="text-[#333]">|</span>
        <Camera className="w-4 h-4 text-[#E50000]" />
        <span><strong className="text-white">+10 pts</strong> por vídeo humanizado (cara + voz)</span>
        <span className="text-[#333]">|</span>
        <span>Ranking acumulado da edição. Voice of KW recalcula mensalmente.</span>
      </div>

      {/* Desktop table */}
      <div className="kw-card overflow-x-auto hidden md:block" data-testid="social-ranking-table">
        <table className="w-full text-left text-sm">
          <thead className="kw-thead-sticky border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-4 w-16 kw-th-sticky-col">#</th>
              <th className="p-4 w-12"></th>
              <th className="p-4">Consultor</th>
              <th className="p-4">Clube</th>
              <th className="p-4 text-right">Posts</th>
              <th className="p-4 text-right">Vídeos</th>
              <th className="p-4 text-right">Pts Sociais</th>
            </tr>
          </thead>
          <tbody>
            {!hasAny && (
              <tr><td colSpan="7" className="p-8 text-center text-[#737373]">Ninguém publicou ainda. Sê o primeiro a marcar voz na KW Lead.</td></tr>
            )}
            {hasAny && rows.filter((r) => r.social_points > 0).map((r) => (
              <tr key={r.player_id} className="border-b border-[#1A1A1A] hover:bg-[#141414] bg-[#1A1A1A]" data-testid={`social-row-${r.player_id}`}>
                <td className="p-4 font-display text-2xl font-black kw-col-sticky">
                  <span className={r.rank === 1 ? "text-[#E50000]" : ""}>{String(r.rank).padStart(2, "0")}</span>
                </td>
                <td className="p-4"><Avatar photoUrl={r.photo_url} name={r.name} size={36} /></td>
                <td className="p-4 font-semibold">
                  {r.name}
                  {voice_of_kw && voice_of_kw.player_id === r.player_id && (
                    <Crown className="inline w-4 h-4 ml-2 text-[#E50000]" />
                  )}
                </td>
                <td className="p-4">{r.club ? <MbtiBadge club={r.club} /> : <span className="text-[#737373] text-xs">—</span>}</td>
                <td className="p-4 text-right">{r.social_posts}</td>
                <td className="p-4 text-right">{r.humanized_videos}</td>
                <td className="p-4 text-right font-display text-xl font-black text-[#E50000]">{Math.round(r.social_points)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3" data-testid="social-ranking-cards">
        {!hasAny && <div className="kw-card p-6 text-center text-[#737373]">Ninguém publicou ainda.</div>}
        {hasAny && rows.filter((r) => r.social_points > 0).map((r) => (
          <div key={r.player_id} className="kw-card p-4 flex items-center gap-3">
            <div className={`font-display text-3xl font-black w-10 text-center ${r.rank === 1 ? "text-[#E50000]" : "text-white"}`}>
              {String(r.rank).padStart(2, "0")}
            </div>
            <Avatar photoUrl={r.photo_url} name={r.name} size={44} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">
                {r.name}
                {voice_of_kw && voice_of_kw.player_id === r.player_id && (
                  <Crown className="inline w-3 h-3 ml-1 text-[#E50000]" />
                )}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                {r.social_posts} posts · {r.humanized_videos} vídeos
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-black text-[#E50000]">{Math.round(r.social_points)}</div>
              <div className="kw-overline">Pts</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoachesView({ data, scope, setScope }) {
  if (!data) {
    return <div className="kw-card p-6 text-center text-[#737373]" data-testid="coaches-loading">A carregar ranking dos secretários…</div>;
  }
  const rows = data.coaches || [];
  return (
    <div className="space-y-6" data-testid="coaches-ranking">
      {/* Header + scope toggle */}
      <div className="kw-card p-4 flex flex-wrap items-center gap-4">
        <GraduationCap className="w-5 h-5 text-[#E50000]" />
        <div className="flex-1 min-w-[200px]">
          <div className="kw-overline text-[#E50000]">Campeonato dos Secretários</div>
          <p className="text-xs text-[#A3A3A3] mt-1">
            Mede mentoria (+5 por mentorando), engagement do clube (% que reportou),
            reuniões realizadas, preenchimento próprio de perfil e Growth Share
            (recrutamento externo +100, interno +20).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScope("week")}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
              scope === "week"
                ? "border-[#E50000] bg-[#E50000] text-white"
                : "border-[#222] text-[#A3A3A3] hover:text-white"
            }`}
            data-testid="coaches-scope-week"
          >
            Semana {data.current_week}
          </button>
          <button
            onClick={() => setScope("month")}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
              scope === "month"
                ? "border-[#E50000] bg-[#E50000] text-white"
                : "border-[#222] text-[#A3A3A3] hover:text-white"
            }`}
            data-testid="coaches-scope-month"
          >
            Últimas 4 sem.
          </button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="kw-card overflow-x-auto hidden md:block" data-testid="coaches-ranking-table">
        <table className="w-full text-left text-sm">
          <thead className="kw-thead-sticky border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-4 w-16 kw-th-sticky-col">#</th>
              <th className="p-4">Secretário</th>
              <th className="p-4">Clube</th>
              <th className="p-4 text-right" title="Mentorandos ativos (+5 cada)">Mentor.</th>
              <th className="p-4 text-right" title="% do clube que reportou">% Report.</th>
              <th className="p-4 text-right" title="Reuniões realizadas (+3 cada)">Reun.</th>
              <th className="p-4 text-right" title="Campos preenchidos no próprio perfil (+3 cada)">Perfil</th>
              <th className="p-4 text-right" title="Growth Share Externo (+100 cada)">GS Ext.</th>
              <th className="p-4 text-right" title="Growth Share Interno (+20 cada)">GS Int.</th>
              <th className="p-4 text-right">Pontos</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="10" className="p-8 text-center text-[#737373]">Sem coaches ativos.</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.coach_user_id} className="border-b border-[#1A1A1A] hover:bg-[#141414] bg-[#1A1A1A]" data-testid={`coach-row-${c.coach_user_id}`}>
                <td className="p-4 font-display text-2xl font-black kw-col-sticky">
                  <span className={c.rank === 1 ? "text-[#E50000]" : ""}>{String(c.rank).padStart(2, "0")}</span>
                </td>
                <td className="p-4 font-semibold">
                  {c.name}
                  {c.rank === 1 && <Crown className="inline w-4 h-4 ml-2 text-[#E50000]" />}
                </td>
                <td className="p-4">{c.club ? <MbtiBadge club={c.club} /> : <span className="text-[#737373] text-xs">—</span>}</td>
                <td className="p-4 text-right">{c.mentorandos}</td>
                <td className="p-4 text-right font-mono text-xs">{c.avg_report_pct}%</td>
                <td className="p-4 text-right">{c.meetings_realizadas}</td>
                <td className="p-4 text-right">{c.profile_filled}/5</td>
                <td className="p-4 text-right">{c.gs_ext}</td>
                <td className="p-4 text-right">{c.gs_int}</td>
                <td className="p-4 text-right font-display text-xl font-black text-[#E50000]">{Math.round(c.total_points)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3" data-testid="coaches-ranking-cards">
        {rows.length === 0 && <div className="kw-card p-6 text-center text-[#737373]">Sem coaches ativos.</div>}
        {rows.map((c) => (
          <div key={c.coach_user_id} className="kw-card p-4">
            <div className="flex items-center gap-3">
              <div className={`font-display text-3xl font-black w-10 text-center ${c.rank === 1 ? "text-[#E50000]" : "text-white"}`}>
                {String(c.rank).padStart(2, "0")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {c.name}
                  {c.rank === 1 && <Crown className="inline w-3 h-3 ml-1 text-[#E50000]" />}
                </div>
                <div className="mt-1">{c.club && <MbtiBadge club={c.club} />}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-black text-[#E50000]">{Math.round(c.total_points)}</div>
                <div className="kw-overline">Pts</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[10px] uppercase tracking-widest text-[#A3A3A3]">
              <div><div className="text-white font-bold">{c.mentorandos}</div>Mentor.</div>
              <div><div className="text-white font-bold">{c.avg_report_pct}%</div>Report.</div>
              <div><div className="text-white font-bold">{c.meetings_realizadas}</div>Reun.</div>
              <div><div className="text-white font-bold">{c.profile_filled}/5</div>Perfil</div>
              <div><div className="text-white font-bold">{c.gs_ext}</div>GS Ext.</div>
              <div><div className="text-white font-bold">{c.gs_int}</div>GS Int.</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
